/** WebGPU capability negotiation and device lifetime, independent of a scene. */
export interface DeviceContext {
  device: GPUDevice
  context: GPUCanvasContext
  format: GPUTextureFormat
  adapterName: string
  ownsCanvas(): boolean
  dispose(): void
}

// A canvas has one presentation context even when concurrent callers request it.
// Claim before the first await: the newest request wins irrespective of adapter,
// device, or pipeline completion order (including React StrictMode remounts).
const canvasOwners = new WeakMap<HTMLCanvasElement, symbol>()

export async function createDevice(
  canvas: HTMLCanvasElement,
  onError: (message: string) => void,
  onLost: (message: string) => void,
): Promise<DeviceContext> {
  if (!globalThis.isSecureContext) {
    throw new Error('WebGPU needs a secure context. Open this table on localhost or an HTTPS address.')
  }
  if (!navigator.gpu) {
    throw new Error('WebGPU is unavailable in this browser. Open this table in a browser with WebGPU enabled and hardware acceleration turned on.')
  }
  const owner = Symbol('WebGPU canvas owner')
  canvasOwners.set(canvas, owner)
  const ownsCanvas = () => canvasOwners.get(canvas) === owner
  const superseded = () => new DOMException('Renderer initialization was superseded by a newer request for this canvas.', 'AbortError')
  const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' })
  if (!ownsCanvas()) throw superseded()
  if (!adapter) throw new Error('No WebGPU adapter is available. Check hardware acceleration and the browser GPU status, then reload.')
  const device = await adapter.requestDevice({ label: 'Tabletop v2 device' })
  if (!ownsCanvas()) { device.destroy(); throw superseded() }
  const context = canvas.getContext('webgpu')
  if (!context) {
    device.destroy()
    throw new Error('Could not create the WebGPU canvas context. Reload the table in a supported browser.')
  }
  let disposed = false
  const errorListener = (event: GPUUncapturedErrorEvent) => {
    if (!disposed && ownsCanvas()) onError(`WebGPU validation: ${event.error.message}`)
  }
  device.addEventListener('uncapturederror', errorListener)
  void device.lost.then((info) => {
    if (!disposed && ownsCanvas()) onLost(`The graphics device was lost (${info.reason}). ${info.message || 'Reload the table to reconnect to the GPU.'}`)
  })
  const format = navigator.gpu.getPreferredCanvasFormat()
  try {
    context.configure({ device, format, alphaMode: 'opaque' })
  } catch (error) {
    disposed = true
    device.removeEventListener('uncapturederror', errorListener)
    if (ownsCanvas()) canvasOwners.delete(canvas)
    device.destroy()
    throw error
  }
  const info = adapter.info
  return {
    device, context, format, ownsCanvas,
    adapterName: [info?.vendor, info?.architecture, info?.description].filter(Boolean).join(' · ') || 'WebGPU adapter',
    dispose() {
      if (disposed) return
      disposed = true
      device.removeEventListener('uncapturederror', errorListener)
      if (ownsCanvas()) { canvasOwners.delete(canvas); context.unconfigure() }
      device.destroy()
    },
  }
}

export async function checkedShader(device: GPUDevice, code: string, label: string): Promise<GPUShaderModule> {
  const shader = device.createShaderModule({ code, label })
  const info = await shader.getCompilationInfo()
  const errors = info.messages.filter((message) => message.type === 'error')
  if (errors.length) {
    throw new Error(`${label}:\n${errors.map((error) => `${error.lineNum}:${error.linePos} ${error.message}`).join('\n')}`)
  }
  return shader
}
